/** @jsxImportSource ../../i18n */
import React, { forwardRef } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { cx } from '../../theme/cx';

const CONTROL = 'w-full rounded-control border border-line-strong bg-surface-2 text-[15px] text-fg transition-colors duration-150 placeholder:text-fg-subtle hover:border-fg-faint focus:border-gold-500/60 focus:outline-none focus:ring-2 focus:ring-gold-500/20 disabled:cursor-not-allowed disabled:opacity-50';

export const Field = ({ label, hint, error, htmlFor, optional, children, className }) => (
  <div className={className}>
    {label && (
      <label htmlFor={htmlFor} className="mb-2 block text-sm font-medium text-fg-body">
        {label}
        {optional && <span className="ml-1 font-normal text-fg-subtle">({optional})</span>}
      </label>
    )}
    {children}
    {hint && !error && <p className="mt-1.5 text-xs leading-relaxed text-fg-subtle">{hint}</p>}
    {error && <p role="alert" className="mt-1.5 text-xs text-loss-300">{error}</p>}
  </div>
);

export const Input = forwardRef(({ leadingIcon: LeadingIcon, trailing, className, inputClassName, ...rest }, ref) => (
  <div className={cx('relative', className)}>
    {LeadingIcon && <LeadingIcon size={17} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-fg-subtle" aria-hidden="true" />}
    <input ref={ref} className={cx(CONTROL, 'h-11 px-3.5', LeadingIcon && 'pl-10', trailing && 'pr-11', inputClassName)} {...rest} />
    {trailing && <div className="absolute right-1.5 top-1/2 flex -translate-y-1/2 items-center">{trailing}</div>}
  </div>
));

export const Select = forwardRef(({ className, selectClassName, children, ...rest }, ref) => (
  <div className={cx('relative', className)}>
    <select ref={ref} className={cx(CONTROL, 'h-11 cursor-pointer appearance-none pl-3.5 pr-10', selectClassName)} {...rest}>
      {children}
    </select>
    <ChevronDown size={16} className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-fg-subtle" aria-hidden="true" />
  </div>
));

export const Textarea = forwardRef(({ className, ...rest }, ref) => (
  <textarea ref={ref} className={cx(CONTROL, 'min-h-[88px] px-3.5 py-2.5 leading-relaxed', className)} {...rest} />
));

export const SearchInput = forwardRef(({ value, onChange, onClear, clearLabel = 'Effacer la recherche', className, ...rest }, ref) => (
  <Input
    ref={ref}
    type="search"
    value={value}
    onChange={onChange}
    leadingIcon={Search}
    className={className}
    inputClassName="[&::-webkit-search-cancel-button]:appearance-none"
    trailing={value && onClear ? (
      <button type="button" onClick={onClear} aria-label={clearLabel} className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-subtle transition-colors hover:bg-surface-3 hover:text-fg">
        <X size={15} aria-hidden="true" />
      </button>
    ) : null}
    {...rest}
  />
));

export const Checkbox = forwardRef(({ switch: isSwitch = false, className, ...rest }, ref) => (
  <span className={cx('relative inline-flex flex-shrink-0 items-center', className)}>
    <input
      ref={ref}
      type="checkbox"
      role={isSwitch ? 'switch' : undefined}
      className={cx(
        'peer cursor-pointer appearance-none border transition-colors duration-150 disabled:cursor-wait disabled:opacity-50',
        isSwitch
          ? 'h-5 w-9 rounded-full border-line-strong bg-surface-3 checked:border-gold-500 checked:bg-gold-500'
          : 'h-[18px] w-[18px] rounded-[5px] border-line-strong bg-surface-2 checked:border-gold-500 checked:bg-gold-500 hover:border-fg-faint',
      )}
      {...rest}
    />
    {isSwitch ? (
      <span aria-hidden="true" className="pointer-events-none absolute left-[3px] top-1/2 h-3.5 w-3.5 -translate-y-1/2 rounded-full bg-fg-muted transition-transform duration-150 ease-standard peer-checked:translate-x-4 peer-checked:bg-on-gold" />
    ) : (
      <Check aria-hidden="true" size={13} strokeWidth={3} className="pointer-events-none absolute inset-0 m-auto text-on-gold opacity-0 peer-checked:opacity-100" />
    )}
  </span>
));
